/**
 * Detect OneEightyVideoBlock instances on a CMS page and return ready-to-emit
 * VideoObject schema fragments (one per qualifying block).
 *
 * Emits a VideoObject only when ALL of these are present — emitting a partial
 * VideoObject is worse than nothing because Search Console reports it as an
 * error and the rich result is rejected:
 *
 *   - name              (videoName / VideoName)
 *   - description       (videoDescription / VideoDescription, plain text)
 *   - thumbnailUrl      (aprimoVideoThumbnail.url, absolute)
 *   - uploadDate        (parseable ISO 8601)
 *   - embedUrl          (normalized from the block's YouTube or Vimeo embed URL)
 *
 * `contentUrl` is intentionally omitted — YouTube/Vimeo do not expose direct
 * media files and Google accepts `embedUrl` alone.
 *
 * Mirrors the server-side `_structuredDataVideoObject.cshtml` shape with two
 * deliberate differences: this uses `embedUrl` rather than `contentUrl`, and
 * the real `UploadDate` rather than `DateTime.Now` (a bug in that template).
 */
export interface VideoObjectSchema {
  '@type': 'VideoObject'
  'name': string
  'description': string
  'thumbnailUrl': string[]
  'uploadDate': string
  'embedUrl': string
  'inLanguage': string
}

// The CMS already stores embed URLs in YouTubeURL / VimeoURL: the block
// component binds them straight to an <iframe src> (OneEightyVideoBlock.vue:
// `youTubeURL?.value || vimeoURL?.value`), and an iframe only renders an embed
// URL — YouTube/Vimeo block their watch pages with X-Frame-Options. So this
// does NOT derive an embed URL from a watch URL; it NORMALIZES the stored embed
// URL into something safe to inline in a JSON-LD <script>.
//
// Why normalize at all: the stored value carries player query params that are
// HTML-encoded in the CD API payload, e.g.
//   https://player.vimeo.com/video/973362373?badge=0&amp;autopause=0&amp;app_id=58479
// A <script type="application/ld+json"> body is NOT HTML-decoded, so a literal
// `&amp;` would survive into embedUrl and make it an invalid URL in the eyes of
// the Rich Results validator. Rebuilding `origin + pathname` from the parsed URL
// drops the query entirely, sidestepping the encoding problem and emitting a
// clean canonical embed URL.

// Parse a URL, returning null instead of throwing on malformed input. Falls
// back to assuming https:// when the scheme is missing, so a protocol-less
// value entered in the CMS (e.g. "player.vimeo.com/video/123") still resolves.
function parseUrl(raw: string): URL | null {
  for (const candidate of [raw, `https://${raw}`]) {
    try {
      return new URL(candidate)
    }
    catch {
      // try the next candidate
    }
  }
  return null
}

// Strip the query/hash from a stored embed URL, returning a clean
// `origin + pathname` (no trailing slash) that is safe to place in JSON-LD.
// Returns null for unparseable input so the gate rejects the block.
function cleanEmbedUrl(raw: string): string | null {
  const url = parseUrl(raw)
  if (!url)
    return null
  const path = url.pathname.replace(/\/$/, '')
  return `${url.origin}${path}`
}

// Prefer YouTube when both are set — matches the iframe precedence in the
// OneEightyVideoBlock template (`youTubeURL || vimeoURL`) and the CMS-side
// `_structuredDataVideoObject.cshtml` template.
export function resolveEmbedUrl(youTubeUrl?: string | null, vimeoUrl?: string | null): string | null {
  const source = (youTubeUrl?.trim() || vimeoUrl?.trim()) ?? ''
  return source ? cleanEmbedUrl(source) : null
}

// CD API property keys for this block can vary between camelCase (Optimizely's
// default conversion, used by the existing youTubeURL/vimeoURL/aprimoMainImage
// properties) and PascalCase (retained by the CustomContentApiModelFilter on
// the new VideoName/VideoDescription/AprimoVideoThumbnail/UploadDate fields).
// Read both — the first non-null wins.
function pick(block: any, ...keys: string[]): any {
  for (const key of keys) {
    if (block?.[key] != null)
      return block[key]
  }
  return undefined
}

// CDA wraps simple property values as `{ value }`. PropertyAprimoImage is
// unwrapped by CustomContentApiModelFilter as `{ url, propertyDataType }`.
// Accept both shapes for string properties so we tolerate either contract.
function readString(raw: any): string {
  if (raw == null)
    return ''
  if (typeof raw === 'string')
    return raw.trim()
  if (typeof raw?.value === 'string')
    return raw.value.trim()
  return ''
}

// Google wants uploadDate in ISO 8601, ideally timezone-qualified. The CD API
// usually returns a trailing `Z`, but normalize defensively: append `Z` (UTC)
// when a datetime comes back timezone-naive. Returns null for unparseable input
// so the gate rejects the block.
function normalizeUploadDate(raw: string): string | null {
  const s = raw.trim()
  if (!s || !Number.isFinite(Date.parse(s)))
    return null
  // Already timezone-qualified (Z or ±HH:MM / ±HHMM) — keep as-is.
  if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(s))
    return s
  // Date-only (YYYY-MM-DD) is valid ISO 8601 for uploadDate — keep as-is.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s))
    return s
  // Has a time component but no timezone → assume UTC.
  if (s.includes('T'))
    return `${s}Z`
  return s
}

export function extractVideoObjects(content: any, inLanguage = 'en-US'): VideoObjectSchema[] {
  const blocks = [
    ...(content?.bodyBlockArea?.expandedValue ?? []),
    ...(content?.pageBlockArea?.expandedValue ?? []),
  ]
  const videos: VideoObjectSchema[] = []
  for (const block of blocks) {
    const contentTypes: string[] = block?.contentType ?? []
    if (!contentTypes.includes('OneEightyVideoBlock'))
      continue

    const name = readString(pick(block, 'videoName', 'VideoName'))
    const description = readString(pick(block, 'videoDescription', 'VideoDescription'))
    const thumbnail = pick(block, 'aprimoVideoThumbnail', 'AprimoVideoThumbnail')
    const thumbnailUrl = typeof thumbnail?.url === 'string' ? thumbnail.url.trim() : ''
    const uploadDate = normalizeUploadDate(readString(pick(block, 'uploadDate', 'UploadDate')))
    const youTubeUrl = readString(pick(block, 'youTubeURL', 'YouTubeURL'))
    const vimeoUrl = readString(pick(block, 'vimeoURL', 'VimeoURL'))
    const embedUrl = resolveEmbedUrl(youTubeUrl, vimeoUrl)

    // The strict gate: skip the block unless every required field is present.
    // A partial VideoObject is worse than none — it produces Search Console
    // errors and the rich result is rejected.
    if (!name || !description || !thumbnailUrl || !uploadDate || !embedUrl)
      continue

    videos.push({
      '@type': 'VideoObject',
      name,
      description,
      'thumbnailUrl': [thumbnailUrl],
      uploadDate,
      embedUrl,
      // Locale-correct: the CD API already returns culture-specific
      // name/description/thumbnail for the rendered page's locale; tag the
      // node with that locale so multi-language pages stay unambiguous.
      inLanguage,
    })
  }
  return videos
}
